import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { reportingPeriod, emergencyMap } from "@duress/shared";
import {
  computeDashboard,
  computeEmergencyMap,
  deletePeriod,
  getActiveUploadJob,
  getDailyActivity,
  getEmergencyMapForPeriod,
  getPeriod,
  getTeamDeviceBreakdown,
  getTrends,
  getUploadJobStatus,
  listPeriods,
  uploadPeriod,
} from "./api";
import type { UploadPeriodFiles } from "./api";

export type SelectedView =
  | { mode: "period"; periodId: string }
  | { mode: "range"; from: string; to: string }
  | { mode: "alltime" };

export interface TrendPoint {
  periodId: string;
  year: number;
  month: number;
  label: string;
  extended_session_rate: number;
  productive_hours: number;
  fleet_idle_rate_pct: number;
  total_sessions: number;
}

function periodsKey(clientId: string) {
  return ["periods", clientId] as const;
}

function periodDetailKey(clientId: string, periodId: string) {
  return ["periods", clientId, periodId] as const;
}

export function useSelectedPeriod(
  clientId: string,
  periodId: string | null,
): {
  data: reportingPeriod.PeriodDetail | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: periodDetailKey(clientId, periodId ?? ""),
    queryFn: () => {
      if (!periodId) throw new Error("periodId required");
      return getPeriod(clientId, periodId);
    },
    enabled: Boolean(clientId) && Boolean(periodId),
    staleTime: 30_000,
  });
  return { data, isLoading, error };
}

export function usePeriods(clientId: string): {
  data: reportingPeriod.ReportingPeriodListResponse | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: periodsKey(clientId),
    queryFn: () => listPeriods(clientId),
    enabled: Boolean(clientId),
    // Without this the picker refetched on every mount and window refocus.
    // The period list only changes on upload/delete, both of which already
    // invalidate this key explicitly.
    staleTime: 30_000,
  });
  return { data, isLoading, error };
}

interface UploadArgs {
  clientId: string;
  files: UploadPeriodFiles;
}

// A feature-only upload finishes synchronously (UploadPeriodResponse) and
// its caches are invalidated here, same as before. A usage-CSV upload
// instead returns an AsyncUploadResponse ({ jobId, totalMonths }) almost
// immediately — the real work hasn't happened yet, so invalidating caches
// here would be premature. The caller polls useUploadJobStatus and
// invalidates once the job reports "completed" (see UploadPeriodDialog.tsx).
export function useUploadPeriod(clientId: string): {
  mutateAsync: (args: UploadArgs) => Promise<reportingPeriod.UploadOutcome>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (args: UploadArgs) => uploadPeriod(args.clientId, args.files),
    onSuccess: (result) => {
      if ("jobId" in result) return;
      void qc.invalidateQueries({ queryKey: periodsKey(clientId) });
      void qc.invalidateQueries({ queryKey: ["clients"] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}

// Polls a usage-CSV upload's processing job while it's still
// partitioning/processing, so the dialog can show real per-month progress
// instead of an indeterminate spinner for however long the file takes.
// Stops polling once the job reaches a terminal state (completed/failed) or
// is reported stalled (see upload-job.repository.ts's staleness check).
export function useUploadJobStatus(
  clientId: string,
  jobId: string | null,
): {
  data: reportingPeriod.UploadJobStatus | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ["upload-jobs", clientId, jobId ?? ""] as const,
    queryFn: () => {
      if (!jobId) throw new Error("jobId required");
      return getUploadJobStatus(clientId, jobId);
    },
    enabled: Boolean(clientId) && Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const stalled = query.state.data?.stalled;
      if (!status || stalled) return false;
      return status === "partitioning" || status === "processing" ? 1500 : false;
    },
    // A large upload can run for many minutes — long enough that the
    // background-tab timer throttling most browsers apply can stall the
    // 1.5s poll above well past when the job actually finished server-side.
    // The app disables this globally (see App.tsx's QueryClient) since most
    // queries don't need it, but this one is watching a job the user is
    // actively waiting on, so catching up the moment the tab regains focus
    // matters more here than the extra request it costs elsewhere.
    refetchOnWindowFocus: true,
  });
  return { data, isLoading, error };
}

// Whether this client has an in-flight usage upload right now, checked
// independently of any UploadPeriodDialog instance's in-memory jobId — that
// state doesn't survive the dialog closing, a page refresh, or a new tab,
// so this is what lets the client page (and a freshly (re)opened dialog)
// notice a job that's already running and resume watching it instead of
// losing track the moment nobody's polling its jobId anymore.
export function useActiveUploadJob(clientId: string): {
  jobId: string | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ["upload-jobs", clientId, "active"] as const,
    queryFn: () => getActiveUploadJob(clientId),
    enabled: Boolean(clientId),
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });
  return { jobId: data?.jobId ?? null, isLoading };
}

function computeViewKey(clientId: string, from: string | undefined, to: string | undefined) {
  return ["sessions-compute", clientId, from ?? "alltime", to ?? "alltime"] as const;
}

export function useDashboardView(
  clientId: string,
  view: SelectedView | null,
): {
  data: reportingPeriod.DashboardData | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const isPeriodMode = !view || view.mode === "period";
  const periodId = view?.mode === "period" ? view.periodId : null;
  const computeFrom = view?.mode === "range" ? view.from : undefined;
  const computeTo = view?.mode === "range" ? view.to : undefined;

  const {
    data: periodData,
    isLoading: periodLoading,
    error: periodError,
    refetch: refetchPeriod,
  } = useQuery({
    queryKey: periodDetailKey(clientId, periodId ?? ""),
    queryFn: () => {
      if (!periodId) throw new Error("periodId required");
      return getPeriod(clientId, periodId);
    },
    enabled: Boolean(clientId) && isPeriodMode && Boolean(periodId),
    staleTime: 30_000,
  });

  const {
    data: computeData,
    isLoading: computeLoading,
    error: computeError,
    refetch: refetchCompute,
  } = useQuery({
    queryKey: computeViewKey(clientId, computeFrom, computeTo),
    queryFn: () =>
      computeDashboard(
        clientId,
        computeFrom && computeTo ? { from: computeFrom, to: computeTo } : undefined,
      ),
    enabled: Boolean(clientId) && !isPeriodMode,
    staleTime: 30_000,
  });

  if (isPeriodMode) {
    return {
      data: periodData?.dashboardData,
      isLoading: periodLoading,
      error: periodError,
      refetch: () => void refetchPeriod(),
    };
  }
  return {
    data: computeData?.dashboardData,
    isLoading: computeLoading,
    error: computeError,
    refetch: () => void refetchCompute(),
  };
}

function emergencyMapPeriodKey(clientId: string, periodId: string) {
  return ["emergency-map", "period", clientId, periodId] as const;
}

function emergencyMapComputeKey(clientId: string, from: string | undefined, to: string | undefined) {
  return ["emergency-map", "compute", clientId, from ?? "alltime", to ?? "alltime"] as const;
}

// Polls while any address is still resolving — geocoding runs in the
// background (see EmergencyMapService's waitUntil trigger) after the first
// request that discovers a new address, so a fixed interval catches the
// pending -> resolved transition without the user needing to manually
// refresh.
function pendingGeocodePollInterval(query: {
  state: { data?: emergencyMap.EmergencyMapResponse | undefined };
}): number | false {
  return query.state.data && query.state.data.pendingGeocodeCount > 0 ? 8000 : false;
}

export function useEmergencyMap(
  clientId: string,
  view: SelectedView | null,
): {
  data: emergencyMap.EmergencyMapResponse | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const isPeriodMode = !view || view.mode === "period";
  const periodId = view?.mode === "period" ? view.periodId : null;
  const computeFrom = view?.mode === "range" ? view.from : undefined;
  const computeTo = view?.mode === "range" ? view.to : undefined;

  const {
    data: periodData,
    isLoading: periodLoading,
    error: periodError,
    refetch: refetchPeriod,
  } = useQuery({
    queryKey: emergencyMapPeriodKey(clientId, periodId ?? ""),
    queryFn: () => {
      if (!periodId) throw new Error("periodId required");
      return getEmergencyMapForPeriod(clientId, periodId);
    },
    enabled: Boolean(clientId) && isPeriodMode && Boolean(periodId),
    staleTime: 30_000,
    refetchInterval: pendingGeocodePollInterval,
  });

  const {
    data: computeData,
    isLoading: computeLoading,
    error: computeError,
    refetch: refetchCompute,
  } = useQuery({
    queryKey: emergencyMapComputeKey(clientId, computeFrom, computeTo),
    queryFn: () =>
      computeEmergencyMap(
        clientId,
        computeFrom && computeTo ? { from: computeFrom, to: computeTo } : undefined,
      ),
    enabled: Boolean(clientId) && !isPeriodMode,
    staleTime: 30_000,
    refetchInterval: pendingGeocodePollInterval,
  });

  if (isPeriodMode) {
    return {
      data: periodData,
      isLoading: periodLoading,
      error: periodError,
      refetch: () => void refetchPeriod(),
    };
  }
  return {
    data: computeData,
    isLoading: computeLoading,
    error: computeError,
    refetch: () => void refetchCompute(),
  };
}

export function useMonthlyTrends(clientId: string): {
  trends: TrendPoint[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["periods", clientId, "trends"] as const,
    queryFn: () => getTrends(clientId),
    enabled: Boolean(clientId),
    staleTime: 30_000,
  });

  const trends: TrendPoint[] = (data?.items ?? []).map((item) => ({
    periodId: item.periodId,
    year: item.year,
    month: item.month,
    label: item.label,
    extended_session_rate: item.extended_session_rate,
    productive_hours: item.productive_hours,
    fleet_idle_rate_pct: item.fleet_idle_rate_pct,
    total_sessions: item.total_sessions,
  }));

  return { trends, isLoading, error, refetch: () => void refetch() };
}

export function useDailyActivity(
  clientId: string,
  periodId: string | null,
): {
  data: reportingPeriod.DailyActivityResponse | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["periods", clientId, periodId ?? "", "daily-activity"] as const,
    queryFn: () => {
      if (!periodId) throw new Error("periodId required");
      return getDailyActivity(clientId, periodId);
    },
    enabled: Boolean(clientId) && Boolean(periodId),
    staleTime: 30_000,
  });
  return { data, isLoading, error, refetch: () => void refetch() };
}

export function useTeamDeviceBreakdown(
  clientId: string,
  periodId: string | null,
  team: string | null,
): {
  data: reportingPeriod.TeamDeviceBreakdownResponse | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ["periods", clientId, periodId ?? "", "devices", team ?? ""] as const,
    queryFn: () => {
      if (!periodId || !team) throw new Error("periodId and team required");
      return getTeamDeviceBreakdown(clientId, periodId, team);
    },
    enabled: Boolean(clientId) && Boolean(periodId) && Boolean(team),
    staleTime: 30_000,
  });
  return { data, isLoading, error };
}

export function useDeletePeriod(clientId: string): {
  mutateAsync: (periodId: string) => Promise<void>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (periodId: string) => deletePeriod(clientId, periodId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: periodsKey(clientId) });
      void qc.invalidateQueries({ queryKey: ["clients"] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}
